import { Fragment, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useSession } from '@/lib/hooks/useSession';
import { useOnboardingFlow } from '@/features/onboarding/OnboardingFlowProvider';

interface OnboardingGuardProps {
  children: ReactNode;
}

const isOnboardingRoute = (pathname: string): boolean =>
  pathname.startsWith('/onboarding/payment') || pathname.startsWith('/onboarding/questionnaire');

const resolveOnboardingDestination = (paymentComplete: boolean | undefined): string =>
  paymentComplete ? '/onboarding/questionnaire' : '/onboarding/payment';

const OnboardingGuard = ({ children }: OnboardingGuardProps): JSX.Element | null => {
  const session = useSession();
  const flow = useOnboardingFlow();
  const location = useLocation();

  if (session.loading) {
    return null;
  }

  if (!session.authenticated) {
    if (!flow.hydrated) {
      return null;
    }
    if (flow.hasSignupContext) {
      const destination = resolveOnboardingDestination(flow.paymentComplete);
      if (location.pathname !== destination) {
        return <Navigate to={destination} replace />;
      }
      return null;
    }
    return <Navigate to="/auth/login" replace state={{ from: location }} />;
  }

  if (
    session.data.onboardingStatus === 'pending' &&
    flow.hydrated &&
    !isOnboardingRoute(location.pathname)
  ) {
    const destination = resolveOnboardingDestination(flow.paymentComplete);
    if (location.pathname !== destination) {
      return <Navigate to={destination} replace />;
    }
  }

  return <Fragment>{children}</Fragment>;
};

export default OnboardingGuard;
